import * as React from 'react';

import { Autocomplete, TextField, Typography } from '@mui/material';

import { tokenKey, type TokenOption } from './tokenSearch';

/**
 * Token search, as a lens rather than a list.
 *
 * The same vocabulary that used to produce a table of custom candidates now
 * lights up points on the map, so a product is chosen from where it sits in the
 * space rather than from its position in a ranked list.
 */

type Props = {
  vocabulary: TokenOption[];
  value: TokenOption[];
  onChange: (next: TokenOption[]) => void;
  matchCount: number;
  loading: boolean;
  /** the token views are not present in this environment */
  unavailable?: boolean;
};

const TokenFilter: React.FC<Props> = ({
  vocabulary,
  value,
  onChange,
  matchCount,
  loading,
  unavailable = false,
}) =>
  unavailable ? (
    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
      Token search is unavailable here — the perception model token views have
      not been created in this environment. The map is unaffected.
    </Typography>
  ) : (
    <>
      <Autocomplete
        multiple
        options={vocabulary}
        value={value}
        loading={loading}
        groupBy={(o) => o.kind}
        getOptionLabel={(o) => `${o.kind}: ${o.token}`}
        isOptionEqualToValue={(a, b) => tokenKey(a) === tokenKey(b)}
        onChange={(_, next) => onChange(next as TokenOption[])}
        renderOption={(props, option) => (
          <li {...props} key={tokenKey(option)}>
            {option.token}
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 1, color: 'text.secondary' }}
            >
              {option.productCount}
            </Typography>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Highlight by taste"
            placeholder={loading ? 'Loading…' : 'e.g. cooling, fizzy, cream'}
          />
        )}
      />

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 0.5, mb: 1, color: 'text.secondary' }}
      >
        {value.length === 0
          ? 'Pick tokens to light up the products that carry all of them.'
          : `${matchCount} product${matchCount === 1 ? '' : 's'} carry every token selected.`}
      </Typography>
    </>
  );

export default TokenFilter;
